import { DocumentNode, gql, OperationVariables, QueryResult, useLazyQuery } from '@apollo/client';
import { NativeSelect } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getLocaleLabel, localeKeys } from '../../config/i18next';
import { ESPACE_PRIVE, EVENEMENTS, NON_DISPONIBLE, PARCOURS_FORMATION, ROUTE_MODEL_MAP } from '../../constants/routing';
import { StrapiLocalizableEntity } from '../../models/layout';
import { ModelName } from '../../models/routes';
import { IconChevron } from '../icons/IconChevron';

type LanguageSelectorProps = { locale: string };
type Params = { slug: string };

type LanguageSelectorGqlMap = { [key: string]: DocumentNode };

type CollectionSlugQuery = {
  briqueSavoirs?: StrapiLocalizableEntity[];
  parcoursFormations?: StrapiLocalizableEntity[];
  quizs?: StrapiLocalizableEntity[];
  pagePubliques?: StrapiLocalizableEntity[];
  pagePrivees?: StrapiLocalizableEntity[];
  evenements?: StrapiLocalizableEntity[];
};

const translateSlug = (
  modelName: ModelName,
  newlocale: string,
  query: QueryResult<CollectionSlugQuery, OperationVariables>
) => {
  const actualCollection = query?.data?.[modelName]?.[0];
  const associatedCollections = actualCollection?.localizations ?? [];
  const translatedCollection = associatedCollections.find((col) => col.locale === newlocale);
  return translatedCollection?.Slug;
};

const getTypeOfContent = (pathArray: string[], isPublic: boolean) => {
  if (isPublic) return ROUTE_MODEL_MAP['page-publique'];
  if (pathArray[1] === EVENEMENTS) return EVENEMENTS;
  if (pathArray[1] === PARCOURS_FORMATION) return ROUTE_MODEL_MAP[pathArray[1]];
  return ROUTE_MODEL_MAP['page-privee'];
};

export const LanguageSelector = ({ locale }: LanguageSelectorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams<Params>() ?? '';
  const pathArray = location.pathname.split('/').slice(2);
  const isPublic = pathArray[0] !== ESPACE_PRIVE;
  const type = getTypeOfContent(pathArray, isPublic);
  const queryGql = LANGUAGE_SELECTOR_QUERIES[type];
  const [loadSlugs] = useLazyQuery<CollectionSlugQuery>(queryGql);

  const switchLanguage = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newlocale = event.target.value.toLowerCase();
      // Les pages "détail événement" ne sont pas localisées, inutile de traduire le slug.
      if (!slug || type === EVENEMENTS) {
        navigate(`/${newlocale}/${pathArray.join('/')}`);
        return;
      }

      loadSlugs({
        variables: { locale, slug },
        context: { isPublic: isPublic }
      })
        .then((query) => {
          const newSlug = translateSlug(type, newlocale, query);
          let newPath;
          if (newSlug) {
            newPath = isPublic ? newSlug : ESPACE_PRIVE + '/' + newSlug;
          } else {
            newPath = NON_DISPONIBLE;
          }
          navigate(`/${newlocale}/${newPath}`);
        })
        .catch((err) => {
          console.error('switchLanguage: %o', err);
          navigate(`/${newlocale}/${NON_DISPONIBLE}`);
        });
    },
    [slug, pathArray, navigate, loadSlugs]
  );
  return (
    <NativeSelect
      aria-label={t('header.languageSelector') ?? ''}
      IconComponent={IconChevron}
      value={locale.toUpperCase()}
      onChange={switchLanguage}
      sx={{
        color: 'primary.main',
        fontWeight: 600,
        '&:before, &:focus:before': {
          display: 'none!important'
        },
        '&:hover': {
          color: 'text.primary'
        },
        'select.MuiNativeSelect-select.MuiInput-input': {
          letterSpacing: '1px',
          pr: '1.75rem'
        },
        svg: {
          position: 'absolute',
          top: 0,
          right: 0,
          pointerEvents: 'none',
          height: '100%',
          width: '1.5rem'
        }
      }}
    >
      {localeKeys.map((lang) => (
        <option
          key={lang}
          lang={lang}
          aria-label={getLocaleLabel(lang.toLowerCase())}
          aria-current={lang.toLowerCase() === locale ? 'true' : undefined}
        >
          {lang.toUpperCase()}
        </option>
      ))}
    </NativeSelect>
  );
};

const LANGUAGE_SELECTOR_QUERIES: LanguageSelectorGqlMap = {};

for (const modelName of Object.values(ROUTE_MODEL_MAP)) {
  LANGUAGE_SELECTOR_QUERIES[modelName] = gql`
    query GetLanguageSelector${modelName}($locale: I18NLocaleCode!, $slug: String!) {
      ${modelName}(locale: $locale, filters: { Slug: { eq: $slug } }) {
        Slug
        locale
        localizations {
          locale
          Slug
        }
      }
    }
  `;
}
